import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    LinearProgress,
    Card,
    CardContent,
    Grid,
    Divider,
    Tabs,
    Tab,
    Chip,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Checkbox,
    FormControlLabel,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { getReviewSummary, getFullReviewSummary, addReviewerComments, finalizeReview, getReviewStatus, getLLMProviderAndModelList, generateReviewSummary, downloadReviewSummaryPdfReport } from '../../services/api';
import { getUser } from '../../services/auth';
import { UserUtils } from '../../types';
import { formatDisplayDate } from '../../utils/dateUtils';
import './MarkdownStyles.css';

/** GFM tables + horizontal scroll wrapper for wide LLM tables */
const reviewSummaryMarkdownProps = {
    remarkPlugins: [remarkGfm],
    components: {
        table: (props: React.ComponentPropsWithoutRef<'table'> & { node?: unknown }) => {
            const { node: _n, ...rest } = props;
            return (
                <div className="markdown-table-wrap">
                    <table {...rest} />
                </div>
            );
        },
    },
};

/** Server message for axios errors (FastAPI `detail` string, array, or raw body). */
function getApiErrorDetail(err: unknown, fallback: string): string {
    if (err && typeof err === 'object' && 'response' in err) {
        const data = (err as { response?: { data?: unknown } }).response?.data;
        if (typeof data === 'string') {
            return data;
        }
        if (data && typeof data === 'object') {
            const d = data as { detail?: unknown; message?: unknown };
            if (typeof d.detail === 'string') {
                return d.detail;
            }
            if (Array.isArray(d.detail)) {
                return d.detail
                    .map((item) =>
                        typeof item === 'object' && item !== null && 'msg' in item
                            ? String((item as { msg: string }).msg)
                            : JSON.stringify(item)
                    )
                    .join('; ');
            }
            if (typeof d.message === 'string') {
                return d.message;
            }
        }
    }
    if (err instanceof Error && err.message) {
        return err.message;
    }
    return fallback;
}

interface RaidItemDetail {
    detail_id: number;
    raid_id: number;
    details: any;
    status: string;
    created_at: string;
}

interface RaidItem {
    title: string;
    category: string;
    entity_type: string;
    details: RaidItemDetail[];
}

interface MinimalQuestionAnswer {
    answer_value: any;
    review_question_text: string;
    review_dimension: string;
    answer_type: string;
    default_value: any;
    ideal_value: any;
    raid_items: RaidItem[];
}

interface MinimalQuestionAnswerByDimension {
    [dimensionName: string]: MinimalQuestionAnswer[];
}

interface DimensionAnswerDetail {
    total_questions: number;
    answered_questions: number;
    non_ideal_answers: number;
    raid_items: number;
    non_ideal_answers_without_raid_items: number;
}

interface DimensionAnswerDetails {
    [dimensionName: string]: DimensionAnswerDetail;
}

interface ComputedMetric {
    computed_metric_id: number;
    review_entity_id: number;
    metric_date: string;
    metric_identifier_type: string;
    metric_identifier_name: string;
    metric_type: string;
    metric_name: string;
    metric_value: number;
}

interface ReviewSummary {
    dimension_answer_details: DimensionAnswerDetails;
    computed_metrics_by_dimension: ComputedMetric[];
    minimal_question_answer_by_dimension: MinimalQuestionAnswerByDimension;
    last_metric_date: string | null;
}

// Flattened RAID item for display in the table
interface FlattenedRaidItem {
    title: string;
    category: string;
    entity_type: string;
    created_at: string;
    dimension: string;
}

interface TextualReviewSummary {
    review_questionnaire_response_id: number;
    llm_generated_summary: string;
    reviewer_comments: string;
    llm_provider: string;
    llm_model: string;
    llm_context_id: string;
    id: number;
    reviewer_id: number;
    status?: string;
    created_by_user?: {
        id: number;
        username: string;
        first_name: string;
        last_name: string;
    } | null;
    created_at?: string;
    updated_at?: string;
}

interface LLMProviderModel {
    provider: string;
    model: string;
}

interface ReviewSummaryDialogProps {
    open: boolean;
    onClose: () => void;
    responseId: number;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 1 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const ReviewSummaryDialog: React.FC<ReviewSummaryDialogProps> = ({
    open,
    onClose,
    responseId
}) => {
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string>('');
    const [summary, setSummary] = React.useState<ReviewSummary | null>(null);
    const [textSummaries, setTextSummaries] = React.useState<TextualReviewSummary[]>([]);
    const [textSummaryLoading, setTextSummaryLoading] = React.useState(false);
    const [textSummaryError, setTextSummaryError] = React.useState<string>('');
    const [activeTab, setActiveTab] = React.useState(0);
    const [reviewerComments, setReviewerComments] = React.useState<string>('');
    const [isSavingComments, setIsSavingComments] = React.useState(false);
    const [isClosingReview, setIsClosingReview] = React.useState(false);
    const [commentsSaved, setCommentsSaved] = React.useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
    const [reviewStatus, setReviewStatus] = React.useState<string>('');
    
    // LLM Provider and Model selection state
    const [llmProviderModelList, setLlmProviderModelList] = React.useState<LLMProviderModel[]>([]);
    const [selectedProvider, setSelectedProvider] = React.useState<string>('');
    const [selectedModel, setSelectedModel] = React.useState<string>('');
    const [saveSummary, setSaveSummary] = React.useState<boolean>(true);
    const [overrideExistingSummary, setOverrideExistingSummary] = React.useState<boolean>(false);
    const [isGeneratingSummary, setIsGeneratingSummary] = React.useState(false);
    const [generatedSummary, setGeneratedSummary] = React.useState<TextualReviewSummary | null>(null);
    const [errorDialogOpen, setErrorDialogOpen] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const [flattenedRaidItems, setFlattenedRaidItems] = React.useState<FlattenedRaidItem[]>([]);
    const [pdfDownloading, setPdfDownloading] = React.useState(false);

    // Get current user information
    const currentUser = getUser();
    const canPerformReview = UserUtils.canPerformReview(currentUser);
    const reviewerId = currentUser?.id;
    
    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);

        const hasR = flattenedRaidItems.length > 0;
        const textualIdx = hasR ? 2 : 1;
        const closureIdx = hasR ? 3 : 2;

        const shouldPrefetchTextSummaries =
            (newValue === textualIdx || newValue === closureIdx) &&
            textSummaries.length === 0 &&
            !textSummaryLoading &&
            responseId;

        if (shouldPrefetchTextSummaries) {
            fetchTextualSummary();
        }
    };

    const handleDownloadPdfReport = async () => {
        if (!responseId) return;
        setPdfDownloading(true);
        try {
            await downloadReviewSummaryPdfReport(Number(responseId));
        } catch (err) {
            setErrorMessage(getApiErrorDetail(err, 'Failed to download PDF report.'));
            setErrorDialogOpen(true);
        } finally {
            setPdfDownloading(false);
        }
    };

    // Function to fetch the review status
    const fetchReviewStatus = () => {
        if (responseId) {
            const numericResponseId = Number(responseId);
            console.log(`Fetching review status for response ID:`, numericResponseId);
            
            getReviewStatus(numericResponseId)
                .then(response => {
                    console.log('Review status API response:', response.data);
                    // API returns plain string like "SUBMITTED", not a JSON object
                    if (response.data) {
                        const status = typeof response.data === 'string' ? response.data : response.data.status;
                        setReviewStatus(status);
                        console.log('Review status set to:', status);
                    } else {
                        console.warn('No status in response:', response.data);
                    }
                })
                .catch(err => {
                    console.error('Error fetching review status:', err);
                });
        }
    };

    // Function to fetch LLM provider and model list
    const fetchLLMProviderModelList = () => {
        console.log('Fetching LLM provider and model list');
        
        getLLMProviderAndModelList()
            .then(response => {
                console.log('LLM provider and model list response:', response.data);
                if (response.data && response.data.llm_provider_and_model_list) {
                    setLlmProviderModelList(response.data.llm_provider_and_model_list);
                    // Set default selections if available
                    if (response.data.llm_provider_and_model_list.length > 0) {
                        const firstItem = response.data.llm_provider_and_model_list[0];
                        setSelectedProvider(firstItem.provider);
                        setSelectedModel(firstItem.model);
                    }
                } else {
                    console.warn('No LLM provider and model list in response:', response.data);
                }
            })
            .catch(err => {
                console.error('Error fetching LLM provider and model list:', err);
            });
    };

    // Function to generate review summary
    const handleGenerateSummary = () => {
        if (!selectedProvider || !selectedModel || !responseId) {
            alert('Please select both provider and model before generating summary.');
            return;
        }

        setIsGeneratingSummary(true);
        setGeneratedSummary(null);
        
        const request = {
            review_questionnaire_response_id: Number(responseId),
            llm_provider: selectedProvider,
            llm_model: selectedModel,
            save_summary: saveSummary,
            override_existing_summary: overrideExistingSummary,
        };

        console.log('Generating summary with request:', request);
        
        generateReviewSummary(request)
            .then(response => {
                console.log('Generate summary response:', response.data);
                if (response.data) {
                    setGeneratedSummary(response.data);
                    if (saveSummary) {
                        // If saved, also refresh the existing textual summary
                        fetchTextualSummary();
                    }
                } else {
                    console.warn('No data in generate summary response:', response.data);
                }
            })
            .catch((err) => {
                console.error('Error generating summary:', err);
                const errorMsg = getApiErrorDetail(
                    err,
                    'An unexpected error occurred while generating the summary.'
                );
                setErrorMessage(errorMsg);
                setErrorDialogOpen(true);
            })
            .finally(() => {
                setIsGeneratingSummary(false);
            });
    };

    // Function to get unique providers from the list
    const getUniqueProviders = (): string[] => {
        const providers = llmProviderModelList.map(item => item.provider);
        return Array.from(new Set(providers));
    };

    // Function to get models for selected provider
    const getModelsForProvider = (provider: string): string[] => {
        return llmProviderModelList
            .filter(item => item.provider === provider)
            .map(item => item.model);
    };

    // Handle provider change
    const handleProviderChange = (provider: string) => {
        setSelectedProvider(provider);
        const modelsForProvider = getModelsForProvider(provider);
        if (modelsForProvider.length > 0) {
            setSelectedModel(modelsForProvider[0]);
        } else {
            setSelectedModel('');
        }
    };

    React.useEffect(() => {
        if (open && responseId) {
            setLoading(true);
            setError('');
            
            // Enhanced debug logging to inspect the responseId value and type
            console.log(`Fetching review summary for response ID:`, responseId);
            console.log(`responseId type:`, typeof responseId);
            
            // Ensure responseId is a number
            const numericResponseId = Number(responseId);
            console.log(`Converted responseId:`, numericResponseId);
            
            // Fetch review status first
            fetchReviewStatus();
            
            // Fetch LLM provider and model list
            fetchLLMProviderModelList();
            
            getReviewSummary(numericResponseId)
                .then(response => {
                    console.log('Review summary API response:', response.data);
                    if (response.data && response.data.dimension_answer_details) {
                        setSummary(response.data);
                        // If we have status information, save it
                        if (response.data.status) {
                            setReviewStatus(response.data.status);
                            console.log('Review status:', response.data.status);
                        }
                        // Extract and flatten RAID items from minimal_question_answer_by_dimension
                        if (response.data.minimal_question_answer_by_dimension) {
                            const raidItems: FlattenedRaidItem[] = [];
                            Object.entries(response.data.minimal_question_answer_by_dimension).forEach(([dimension, answers]) => {
                                (answers as MinimalQuestionAnswer[]).forEach((answer) => {
                                    if (answer.raid_items && answer.raid_items.length > 0) {
                                        answer.raid_items.forEach((raid) => {
                                            // Get the created_at from the first detail if available
                                            const createdAt = raid.details && raid.details.length > 0 
                                                ? raid.details[0].created_at 
                                                : '';
                                            raidItems.push({
                                                title: raid.title,
                                                category: raid.category,
                                                entity_type: raid.entity_type,
                                                created_at: createdAt,
                                                dimension: dimension
                                            });
                                        });
                                    }
                                });
                            });
                            setFlattenedRaidItems(raidItems);
                            console.log('Extracted RAID items:', raidItems);
                        }
                    } else {
                        console.warn('Invalid response format:', response.data);
                        setError('Invalid response format from server');
                    }
                    setLoading(false);
                })
                .catch(err => {
                    console.error('Error fetching review summary:', err);
                    console.error('Error details:', err.response ? err.response.data : 'No response data');
                    setError(`Failed to load review summary: ${err.message}`);
                    setLoading(false);
                });
        }
    }, [open, responseId]);
    
    const fetchTextualSummary = () => {
        if (responseId) {
            setTextSummaryLoading(true);
            setTextSummaryError('');
            
            // Enhanced debug logging to inspect the responseId value and type
            console.log(`Fetching full textual review summary for response ID:`, responseId);
            console.log(`responseId type:`, typeof responseId);
            
            // Ensure responseId is a number
            const numericResponseId = Number(responseId);
            console.log(`Converted responseId:`, numericResponseId);
            
            getFullReviewSummary(numericResponseId)
                .then(response => {
                    console.log('Full textual review summary API response:', response.data);
                    if (response.data && Array.isArray(response.data)) {
                        setTextSummaries(response.data);
                        // If there are reviewer comments in the most recent summary, populate the comments field
                        if (response.data.length > 0 && response.data[0].reviewer_comments) {
                            setReviewerComments(response.data[0].reviewer_comments);
                        }
                    } else {
                        console.warn('Empty or invalid response for textual summary:', response.data);
                        setTextSummaryError('No textual summary available');
                    }
                    setTextSummaryLoading(false);
                })
                .catch((err) => {
                    console.error('Error fetching textual review summary:', err);
                    console.error('Error details:', err.response ? err.response.data : 'No response data');
                    const detail = getApiErrorDetail(
                        err,
                        'Failed to load textual review summary.'
                    );
                    setTextSummaryError(detail);
                    setErrorMessage(detail);
                    setErrorDialogOpen(true);
                    setTextSummaryLoading(false);
                });
        }
    };
    
    const handleSaveComments = () => {
        if (responseId && currentUser && currentUser.id) {
            setIsSavingComments(true);
            setCommentsSaved(false);
            
            // Ensure responseId is a number
            const numericResponseId = Number(responseId);
            const numericReviewerId = Number(currentUser.id);
            
            console.log(`Saving comments for responseId:`, numericResponseId, `reviewerId:`, numericReviewerId);
            
            addReviewerComments(numericResponseId, reviewerComments, numericReviewerId)
                .then(response => {
                    console.log('Save reviewer comments response:', response);
                    setCommentsSaved(true);
                    
                    // Refresh the textual summary to get the updated comments
                    fetchTextualSummary();
                    
                    // Refresh the review status after saving comments
                    fetchReviewStatus();
                    
                    // Clear the saved flag after a delay
                    setTimeout(() => setCommentsSaved(false), 3000);
                })
                .catch(err => {
                    console.error('Error saving reviewer comments:', err);
                    alert(`Failed to save reviewer comments: ${err.message}`);
                })
                .finally(() => {
                    setIsSavingComments(false);
                });
        } else if (!currentUser || !currentUser.id) {
            alert('Unable to save comments: reviewer ID not available. Please try logging out and back in.');
        } else {
            alert('Missing required information (response ID)');
        }
    };
    
    const handleOpenConfirmDialog = () => {
        setConfirmDialogOpen(true);
    };
    
    const handleCloseConfirmDialog = () => {
        setConfirmDialogOpen(false);
    };
    
    const handleCloseReview = () => {
        if (responseId) {
            setIsClosingReview(true);
            setConfirmDialogOpen(false); // Close the confirmation dialog
            
            // Ensure responseId is a number
            const numericResponseId = Number(responseId);
            console.log(`Closing review with responseId:`, numericResponseId);
            
            finalizeReview(numericResponseId)
                .then(response => {
                    // Update review status after successful closure
                    setReviewStatus("REVIEWED");
                    console.log('Review closed successfully, status set to REVIEWED');
                    onClose(); // Close the dialog after successful closure
                })
                .catch(err => {
                    console.error('Error closing review:', err);
                    console.error('Error details:', err.response ? err.response.data : 'No response data');
                    alert(`Failed to close review: ${err.message}`);
                })
                .finally(() => {
                    setIsClosingReview(false);
                });
        }
    };
    
    const calculateCompletionPercentage = (answered: number, total: number): number => {
        if (total === 0) return 0;
        return Math.round((answered / total) * 100);
    };

    // Helper function to format entity type for display
    const formatEntityType = (entityType: string): string => {
        if (entityType === 'review_question_answer') {
            return 'Question Level';
        } else if (entityType === 'review_entity') {
            return 'Entity Level';
        }
        return entityType;
    };

    // Compute tab indices dynamically based on whether RAID items exist
    const hasRaidItems = flattenedRaidItems.length > 0;
    const raidItemsTabIndex = hasRaidItems ? 1 : -1;
    const textualSummaryTabIndex = hasRaidItems ? 2 : 1;
    const reviewClosureTabIndex = hasRaidItems ? 3 : 2;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            fullWidth
            PaperProps={{
                sx: {
                    width: '100%',
                    maxWidth: 'min(96vw, 1280px)',
                },
            }}
        >
            <DialogTitle>
                Review Summary
                <Tabs value={activeTab} onChange={handleTabChange} aria-label="review summary tabs">
                    <Tab label="Data Points" />
                    {flattenedRaidItems.length > 0 && <Tab label={`RAID Items (${flattenedRaidItems.length})`} />}
                    <Tab label="Textual Summary" />
                    {canPerformReview && <Tab label="Review Closure" />}
                </Tabs>
            </DialogTitle>
            <DialogContent>
                <TabPanel value={activeTab} index={0}>
                    {loading ? (
                        <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
                            <CircularProgress />
                        </Paper>
                    ) : error ? (
                        <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
                            <Typography color="error">{error}</Typography>
                        </Paper>
                    ) : (
                        <Box sx={{ p: 1 }}>
                            {/* Overall Summary Card */}
                            {summary && summary.dimension_answer_details && (() => {
                                const dimensions = Object.values(summary.dimension_answer_details);
                                const totalQuestions = dimensions.reduce((sum, d) => sum + d.total_questions, 0);
                                const totalAnswered = dimensions.reduce((sum, d) => sum + d.answered_questions, 0);
                                const totalRaidItems = dimensions.reduce((sum, d) => sum + d.raid_items, 0);
                                const totalNonIdeal = dimensions.reduce((sum, d) => sum + d.non_ideal_answers, 0);
                                const totalNonIdealWithoutRaid = dimensions.reduce((sum, d) => sum + d.non_ideal_answers_without_raid_items, 0);
                                const completionPercentage = totalQuestions > 0 ? Math.round((totalAnswered / totalQuestions) * 100) : 0;
                                
                                return (
                                    <Card sx={{ mb: 3 }}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>Overall Summary</Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={2.4}>
                                                    <Typography variant="subtitle2">Total Questions</Typography>
                                                    <Typography variant="h4">{totalQuestions}</Typography>
                                                </Grid>
                                                <Grid item xs={12} md={2.4}>
                                                    <Typography variant="subtitle2">Answered</Typography>
                                                    <Typography variant="h4">{totalAnswered}</Typography>
                                                </Grid>
                                                <Grid item xs={12} md={2.4}>
                                                    <Typography variant="subtitle2">Non-Ideal</Typography>
                                                    <Typography variant="h4">{totalNonIdeal}</Typography>
                                                </Grid>
                                                <Grid item xs={12} md={2.4}>
                                                    <Typography variant="subtitle2">RAID Items</Typography>
                                                    <Typography variant="h4">{totalRaidItems}</Typography>
                                                </Grid>
                                                <Grid item xs={12} md={2.4}>
                                                    <Typography variant="subtitle2">Completion</Typography>
                                                    <Typography variant="h4">{completionPercentage}%</Typography>
                                                    <LinearProgress 
                                                        variant="determinate" 
                                                        value={completionPercentage} 
                                                        sx={{ mt: 1, height: 10, borderRadius: 5 }}
                                                        color={completionPercentage === 100 ? "success" : "primary"}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                );
                            })()}
                            
                            {/* Dimension Summary Table */}
                            {summary && summary.dimension_answer_details && (
                                <TableContainer component={Paper}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell><Typography fontWeight="bold">Dimension</Typography></TableCell>
                                                <TableCell align="right"><Typography fontWeight="bold">Total Questions</Typography></TableCell>
                                                <TableCell align="right"><Typography fontWeight="bold">Answered</Typography></TableCell>
                                                <TableCell align="right"><Typography fontWeight="bold">Non-Ideal</Typography></TableCell>
                                                <TableCell align="right"><Typography fontWeight="bold">RAID Items</Typography></TableCell>
                                                <TableCell align="right"><Typography fontWeight="bold">Needs Action</Typography></TableCell>
                                                <TableCell align="right"><Typography fontWeight="bold">Completion</Typography></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {Object.entries(summary.dimension_answer_details).map(([dimensionName, details]) => (
                                                <TableRow key={dimensionName}>
                                                    <TableCell component="th" scope="row">
                                                        {dimensionName}
                                                    </TableCell>
                                                    <TableCell align="right">{details.total_questions}</TableCell>
                                                    <TableCell align="right">{details.answered_questions}</TableCell>
                                                    <TableCell align="right">{details.non_ideal_answers}</TableCell>
                                                    <TableCell align="right">{details.raid_items}</TableCell>
                                                    <TableCell align="right">
                                                        <Chip 
                                                            label={details.non_ideal_answers_without_raid_items}
                                                            color={details.non_ideal_answers_without_raid_items > 0 ? "warning" : "default"}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {calculateCompletionPercentage(details.answered_questions, details.total_questions)}%
                                                        <LinearProgress 
                                                            variant="determinate" 
                                                            value={calculateCompletionPercentage(details.answered_questions, details.total_questions)} 
                                                            sx={{ mt: 1, height: 6, borderRadius: 3 }}
                                                            color={details.answered_questions === details.total_questions ? "success" : "primary"}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    )}
                </TabPanel>
                
                {/* RAID Items Tab - Only shown when there are RAID items */}
                {hasRaidItems && (
                    <TabPanel value={activeTab} index={raidItemsTabIndex}>
                        <Box sx={{ p: 1 }}>
                            <Typography variant="h6" gutterBottom>RAID Items</Typography>
                            <TableContainer component={Paper}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><Typography fontWeight="bold">Type</Typography></TableCell>
                                            <TableCell><Typography fontWeight="bold">Title</Typography></TableCell>
                                            <TableCell><Typography fontWeight="bold">Level</Typography></TableCell>
                                            <TableCell><Typography fontWeight="bold">Dimension</Typography></TableCell>
                                            <TableCell><Typography fontWeight="bold">Date Logged</Typography></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {flattenedRaidItems.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell>
                                                    <Chip 
                                                        label={item.category} 
                                                        size="small"
                                                        color={
                                                            item.category === 'RISK' ? 'error' :
                                                            item.category === 'ISSUE' ? 'warning' :
                                                            item.category === 'DEPENDENCY' ? 'info' :
                                                            item.category === 'ASSUMPTION' ? 'secondary' :
                                                            'default'
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>{item.title}</TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={formatEntityType(item.entity_type)} 
                                                        size="small"
                                                        variant="outlined"
                                                        color={item.entity_type === 'review_entity' ? 'primary' : 'default'}
                                                    />
                                                </TableCell>
                                                <TableCell>{item.dimension}</TableCell>
                                                <TableCell>{item.created_at ? formatDisplayDate(item.created_at) : 'N/A'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </TabPanel>
                )}
                
                <TabPanel value={activeTab} index={textualSummaryTabIndex}>
                    <Box sx={{ p: 3 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                                mb: 2,
                            }}
                        >
                            <Typography variant="h6">Text review summary</Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={pdfDownloading ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
                                disabled={pdfDownloading || !responseId}
                                onClick={() => void handleDownloadPdfReport()}
                            >
                                Download PDF report
                            </Button>
                        </Box>
                        {/* Reviewers can regenerate summaries even after submission. */}
                        {(reviewStatus !== 'SUBMITTED' || canPerformReview) && (
                            <Card sx={{ mb: 3 }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>Generate New Summary</Typography>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={overrideExistingSummary}
                                                        onChange={(e) => setOverrideExistingSummary(e.target.checked)}
                                                        disabled={isGeneratingSummary}
                                                    />
                                                }
                                                label="Override existing saved summary"
                                            />
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: -0.5 }}>
                                                When checked, any saved summaries for this review are replaced with the newly generated one.
                                            </Typography>
                                        </Grid>
                                        {llmProviderModelList.length === 1 ? (
                                            // Show as label when only one option
                                            <>
                                                <Grid item xs={12} md={8}>
                                                    <Typography variant="body1" color="textSecondary">
                                                        Using provider: <strong>{selectedProvider}</strong> and model: <strong>{selectedModel}</strong>
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <Button
                                                        variant="contained"
                                                        onClick={handleGenerateSummary}
                                                        disabled={isGeneratingSummary || !selectedProvider || !selectedModel}
                                                        fullWidth
                                                    >
                                                        {isGeneratingSummary ? (
                                                            <>
                                                                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                                                                Generating...
                                                            </>
                                                        ) : (
                                                            'Generate Summary'
                                                        )}
                                                    </Button>
                                                </Grid>
                                            </>
                                        ) : (
                                            // Show dropdowns when multiple options
                                            <>
                                                <Grid item xs={12} md={4}>
                                                    <FormControl fullWidth>
                                                        <InputLabel>LLM Provider</InputLabel>
                                                        <Select
                                                            value={selectedProvider}
                                                            label="LLM Provider"
                                                            onChange={(e) => handleProviderChange(e.target.value)}
                                                            disabled={isGeneratingSummary}
                                                        >
                                                            {getUniqueProviders().map((provider) => (
                                                                <MenuItem key={provider} value={provider}>
                                                                    {provider}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <FormControl fullWidth>
                                                        <InputLabel>LLM Model</InputLabel>
                                                        <Select
                                                            value={selectedModel}
                                                            label="LLM Model"
                                                            onChange={(e) => setSelectedModel(e.target.value)}
                                                            disabled={isGeneratingSummary || !selectedProvider}
                                                        >
                                                            {getModelsForProvider(selectedProvider).map((model) => (
                                                                <MenuItem key={model} value={model}>
                                                                    {model}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <Button
                                                        variant="contained"
                                                        onClick={handleGenerateSummary}
                                                        disabled={isGeneratingSummary || !selectedProvider || !selectedModel}
                                                        fullWidth
                                                    >
                                                        {isGeneratingSummary ? (
                                                            <>
                                                                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                                                                Generating...
                                                            </>
                                                        ) : (
                                                            'Generate Summary'
                                                        )}
                                                    </Button>
                                                </Grid>
                                            </>
                                        )}
                                    </Grid>
                                </CardContent>
                            </Card>
                        )}

                        {/* Generated Summary Display */}
                        {generatedSummary && (
                            <Card sx={{ mb: 3 }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>Generated Summary</Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="textSecondary">Generated Summary:</Typography>
                                            <Box sx={{ mt: 1, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                                                <div className="markdown-body">
                                                    <ReactMarkdown {...reviewSummaryMarkdownProps}>
                                                        {generatedSummary.llm_generated_summary}
                                                    </ReactMarkdown>
                                                </div>
                                            </Box>
                                        </Grid>
                                        
                                        <Grid item xs={12} mt={2}>
                                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>Summary Metadata:</Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                <Chip 
                                                    label={`Provider: ${generatedSummary.llm_provider}`} 
                                                    variant="outlined" 
                                                    size="small" 
                                                />
                                                <Chip 
                                                    label={`Model: ${generatedSummary.llm_model}`} 
                                                    variant="outlined" 
                                                    size="small" 
                                                />
                                                <Chip 
                                                    label={`Context ID: ${generatedSummary.llm_context_id}`} 
                                                    variant="outlined" 
                                                    size="small" 
                                                />
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        )}

                        {/* Existing Saved Summaries */}
                        {textSummaryLoading ? (
                            <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
                                <CircularProgress />
                            </Paper>
                        ) : textSummaryError ? (
                            <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
                                <Typography color="error">{textSummaryError}</Typography>
                            </Paper>
                        ) : textSummaries.length > 0 ? (
                            <Box>
                                <Typography variant="h6" gutterBottom>Saved Review Summaries ({textSummaries.length})</Typography>
                                {textSummaries.map((summary, index) => {
                                    const userName = summary.created_by_user 
                                        ? `${summary.created_by_user.first_name} ${summary.created_by_user.last_name}` 
                                        : 'Unknown User';
                                    const createdAt = summary.created_at || 'Unknown Date';
                                    
                                    return (
                                        <Accordion key={summary.id || index} defaultExpanded={index === 0}>
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                aria-controls={`summary-${index}-content`}
                                                id={`summary-${index}-header`}
                                            >
                                                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                                    <Typography variant="subtitle1" fontWeight="bold">
                                                        Summary #{textSummaries.length - index}
                                                    </Typography>
                                                    <Typography variant="body2" color="textSecondary">
                                                        Generated by {userName} on {createdAt}
                                                    </Typography>
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12}>
                                                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>Generated Summary:</Typography>
                                                        <Box sx={{ mt: 1, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                                                            <div className="markdown-body">
                                                                <ReactMarkdown {...reviewSummaryMarkdownProps}>
                                                                    {summary.llm_generated_summary}
                                                                </ReactMarkdown>
                                                            </div>
                                                        </Box>
                                                    </Grid>
                                                    
                                                    {summary.reviewer_comments && (
                                                        <Grid item xs={12}>
                                                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>Reviewer Comments:</Typography>
                                                            <Box sx={{ mt: 1, p: 2, bgcolor: '#f8f9fa', borderRadius: 1, whiteSpace: 'pre-line' }}>
                                                                <Typography variant="body1">
                                                                    {summary.reviewer_comments}
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                    )}
                                                    
                                                    <Grid item xs={12}>
                                                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>Summary Metadata:</Typography>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                            <Chip 
                                                                label={`Provider: ${summary.llm_provider}`} 
                                                                variant="outlined" 
                                                                size="small" 
                                                            />
                                                            <Chip 
                                                                label={`Model: ${summary.llm_model}`} 
                                                                variant="outlined" 
                                                                size="small" 
                                                            />
                                                            <Chip 
                                                                label={`Context ID: ${summary.llm_context_id}`} 
                                                                variant="outlined" 
                                                                size="small" 
                                                            />
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </AccordionDetails>
                                        </Accordion>
                                    );
                                })}
                            </Box>
                        ) : (
                            <Paper sx={{ p: 4, textAlign: 'center' }}>
                                <Typography>No saved textual summary available for this review.</Typography>
                            </Paper>
                        )}
                    </Box>
                </TabPanel>
                
                {canPerformReview && (
                    <TabPanel value={activeTab} index={reviewClosureTabIndex}>
                        {textSummaryLoading ? (
                            <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
                                <CircularProgress />
                            </Paper>
                        ) : (
                            <Box sx={{ p: 3 }}>
                                <Card>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <Typography variant="h6">Review Closure</Typography>
                                            {reviewStatus === "REVIEWED" && (
                                                <Chip 
                                                    label="Read Only" 
                                                    color="primary" 
                                                    size="small" 
                                                    sx={{ ml: 2 }} 
                                                />
                                            )}
                                        </Box>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12}>
                                                <Typography variant="body1" sx={{ mb: 1 }}>Reviewer Comments</Typography>
                                                <TextField
                                                    variant="outlined"
                                                    multiline
                                                    rows={6}
                                                    fullWidth
                                                    value={reviewerComments}
                                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReviewerComments(e.target.value)}
                                                    disabled={isSavingComments || isClosingReview || reviewStatus === "REVIEWED"}
                                                    placeholder="Enter your comments about this review..."
                                                    sx={{ mb: 2, maxHeight: '200px', overflow: 'auto' }}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                {reviewStatus !== "REVIEWED" ? (
                                                    <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'flex-start' }}>
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            onClick={handleSaveComments}
                                                            disabled={isSavingComments || isClosingReview || reviewStatus === "REVIEWED"}
                                                        >
                                                            {isSavingComments ? (
                                                                <>
                                                                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                                                                    Saving...
                                                                </>
                                                            ) : commentsSaved ? (
                                                                'Saved!'
                                                            ) : (
                                                                'Save Comments'
                                                            )}
                                                        </Button>
                                                        
                                                        <Button
                                                            variant="contained"
                                                            color="success"
                                                            onClick={handleOpenConfirmDialog}
                                                            disabled={isSavingComments || isClosingReview || reviewStatus === "REVIEWED"}
                                                            sx={{ ml: 2 }}
                                                        >
                                                            {isClosingReview ? (
                                                                <>
                                                                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                                                                    Closing Review...
                                                                </>
                                                            ) : (
                                                                'Close Review'
                                                            )}
                                                        </Button>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ mt: 2 }}>
                                                        <Typography variant="body2" color="textSecondary">
                                                            This review has been closed and is now read-only.
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {commentsSaved && reviewStatus !== "REVIEWED" && (
                                                    <Typography color="success.main" variant="body2" sx={{ mt: 1 }}>
                                                        Comments saved successfully!
                                                    </Typography>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Box>
                        )}
                    </TabPanel>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
            
            {/* Confirmation Dialog for Review Closure */}
            <Dialog
                open={confirmDialogOpen}
                onClose={handleCloseConfirmDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {"Confirm Review Closure"}
                </DialogTitle>
                <DialogContent>
                    <Typography id="alert-dialog-description">
                        Are you sure you want to close this review? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmDialog}>Cancel</Button>
                    <Button onClick={handleCloseReview} color="error" autoFocus>
                        Close Review
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Error Dialog for Summary Generation */}
            <Dialog
                open={errorDialogOpen}
                onClose={() => setErrorDialogOpen(false)}
                aria-labelledby="error-dialog-title"
                aria-describedby="error-dialog-description"
                maxWidth="md"
                fullWidth
            >
                <DialogTitle id="error-dialog-title">
                    Summary Generation Error
                </DialogTitle>
                <DialogContent>
                    <Typography id="error-dialog-description" sx={{ mt: 1 }}>
                        {errorMessage}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setErrorDialogOpen(false)} color="primary" autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default ReviewSummaryDialog;